/**
 * PriceTicker Component
 * 
 * Real-time price display with bid/ask
 * 
 * Version: 6.2.0
 */
import React, { useState, useEffect } from 'react'
import './PriceTicker.css'

interface PriceData {
  symbol: string
  name?: string
  lastPrice: number
  change: number
  changePercent: number
  bid: number
  ask: number
  high: number
  low: number
  volume: number
  timestamp?: Date
}

interface PriceTickerProps {
  symbol: string
  name?: string
  initialPrice?: number
  autoUpdate?: boolean
  showDetails?: boolean
  compact?: boolean
  onPriceClick?: (price: number) => void
  className?: string
}

const PriceTicker: React.FC<PriceTickerProps> = ({
  symbol,
  name,
  initialPrice = 0,
  autoUpdate = false,
  showDetails = true,
  compact = false,
  onPriceClick,
  className = ''
}) => {
  const [price, setPrice] = useState(initialPrice)
  const [priceDirection, setPriceDirection] = useState<'up' | 'down' | 'neutral'>('neutral')
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date())

  // Simulated price updates (in real app, this would be WebSocket)
  useEffect(() => {
    if (!autoUpdate || initialPrice === 0) return

    setPrice(initialPrice)
    const interval = setInterval(() => {
      // Simulate small price changes
      const change = (Math.random() - 0.5) * 2
      const newPrice = price + change
      setPrice(newPrice)
      setPriceDirection(change > 0 ? 'up' : change < 0 ? 'down' : 'neutral')
      setLastUpdate(new Date())
    }, 2000)

    return () => clearInterval(interval)
  }, [autoUpdate, initialPrice, price])

  const formatPrice = (p: number) => {
    return p.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })
  }

  const formatVolume = (v: number) => {
    if (v >= 1000000) return `${(v / 1000000).toFixed(1)}M`
    if (v >= 1000) return `${(v / 1000).toFixed(1)}K`
    return v.toString()
  }

  const isPositive = priceDirection === 'up'
  const isNegative = priceDirection === 'down'

  return (
    <div className={`price-ticker ${compact ? 'compact' : ''} ${isPositive ? 'up' : ''} ${isNegative ? 'down' : ''} ${className}`}>
      {/* Symbol Header */}
      <div className="ticker-header">
        <div className="ticker-symbol-info">
          <span className="ticker-symbol">{symbol}</span>
          {name && <span className="ticker-name">{name}</span>}
        </div>
        {autoUpdate && (
          <div className="ticker-status">
            <span className={`status-dot ${isPositive ? 'up' : isNegative ? 'down' : ''}`} />
            <span className="status-text">LIVE</span>
          </div>
        )}
      </div>

      {/* Main Price */}
      <div className="ticker-price-row">
        <span 
          className={`ticker-price ${isPositive ? 'positive' : ''} ${isNegative ? 'negative' : ''}`}
          onClick={() => onPriceClick?.(price)}
        >
          ₹{formatPrice(price)}
        </span>
        <div className={`ticker-change ${priceDirection === 'up' ? 'positive' : priceDirection === 'down' ? 'negative' : ''}`}>
          <span className="change-arrow">{priceDirection === 'up' ? '▲' : priceDirection === 'down' ? '▼' : '—'}</span>
          <span className="change-value">0.00 (0.00%)</span>
        </div>
      </div>

      {/* Bid/Ask */}
      {showDetails && !compact && (
        <div className="ticker-bid-ask">
          <div className="bid-ask-item bid">
            <span className="label">BID</span>
            <span className="value" onClick={() => onPriceClick?.(price - 0.05)}>
              ₹{formatPrice(price - 0.05)}
            </span>
          </div>
          <div className="bid-ask-item ask">
            <span className="label">ASK</span>
            <span className="value" onClick={() => onPriceClick?.(price + 0.05)}>
              ₹{formatPrice(price + 0.05)}
            </span>
          </div>
        </div>
      )}

      {/* Details Grid */}
      {showDetails && !compact && (
        <div className="ticker-details">
          <div className="detail-item">
            <span className="detail-label">High</span>
            <span className="detail-value">₹{formatPrice(price * 1.02)}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Low</span>
            <span className="detail-value">₹{formatPrice(price * 0.98)}</span>
          </div>
          <div className="detail-item">
            <span className="detail-label">Vol</span>
            <span className="detail-value">{formatVolume(Math.random() * 1000000)}</span>
          </div>
        </div>
      )}

      {/* Flash Animation */}
      {(isPositive || isNegative) && (
        <div className={`ticker-flash ${isPositive ? 'flash-up' : 'flash-down'}`} />
      )}
    </div>
  )
}

interface MultiTickerProps {
  tickers: PriceData[]
  className?: string
}

export const MultiTicker: React.FC<MultiTickerProps> = ({ tickers, className = '' }) => {
  return (
    <div className={`multi-ticker ${className}`}>
      {tickers.map((ticker) => (
        <PriceTicker
          key={ticker.symbol}
          symbol={ticker.symbol}
          name={ticker.name}
          initialPrice={ticker.lastPrice}
          compact
        />
      ))}
    </div>
  )
}

export default PriceTicker